import type { CalendarGridEvent } from '@/types/calendar/grid'
import { buildMonthRows, monthListEventsFor } from '@/utils/calendar/month-grid'
import { getMonthNames } from '@/utils/calendar/month-names'
import { computed, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

function parseLocalIsoDate(key: string): Date {
  const [year, month, day] = key.split('-').map(Number)
  return new Date(year!, month! - 1, day!)
}

/**
 * Month grid rows and empty-state for prototype month view.
 */
export function useCalendarMonthGrid(
  selectedDate: Ref<Date>,
  today: Ref<Date>,
  eventsByDate: Ref<Map<string, CalendarGridEvent[]>>
) {
  const { locale } = useI18n()
  const monthNamesShort = computed(() => getMonthNames(locale.value).short)

  const monthRows = computed(() =>
    buildMonthRows(selectedDate.value, today.value, monthNamesShort.value)
  )

  const isMonthEmpty = computed(() => {
    const y = selectedDate.value.getFullYear()
    const m = selectedDate.value.getMonth()
    for (const [key] of eventsByDate.value) {
      const d = parseLocalIsoDate(key)
      if (d.getFullYear() === y && d.getMonth() === m) {
        return false
      }
    }
    return true
  })

  const monthEventsFor = (cell: Parameters<typeof monthListEventsFor>[0]) =>
    monthListEventsFor(cell, eventsByDate.value)

  return {
    monthRows,
    isMonthEmpty,
    monthEventsFor
  }
}
